import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import {
  BellRing,
  CalendarCheck2,
  ExternalLink,
  Link as LinkIcon,
  MessageCircleMore,
  Phone,
  Settings,
  Save,
  Star,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthSession } from '../../../contexts/AuthSessionContext';
import { ATENDABOT_PRODUCT_IMAGE } from '../../../branding/assets';
import api, { getGoogleBusinessStatus } from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/apiError';
import ProductCheckoutModal from '../../billing/ProductCheckoutModal';
import GoogleReviewsSettings from './GoogleReviewsSettings';
import { getGoogleLink, patchGoogleLink } from '../FeedbackForm/services/api';
import {
  fetchAtendabotReservasSettings,
  fetchRestaurantSettings,
  updateAtendabotReservasSettings,
  updateRestaurantSettings,
} from '../Reservo/services/api.ts';

const ATENDABOT_WHATSAPP_CONTRACT_URL = String(
  process.env.REACT_APP_ATENDABOT_WHATSAPP_CONTRACT_URL || '',
).trim();
const RESERVO_WHATSAPP_CONTRACT_URL = String(
  process.env.REACT_APP_RESERVO_WHATSAPP_CONTRACT_URL || '',
).trim();

const openExternalLink = (url) => {
  const target = String(url || '').trim();
  if (!target || typeof window === 'undefined') return;
  window.open(target, '_blank', 'noopener,noreferrer');
};

const defaultSettings = {
  usar_reservo_reservas: false,
  usar_feedbackhero_avaliacao_pedidos: false,
  link_reserva_externo: '',
  reservo_link_reservas: '',
};

const defaultReservoSettings = {
  usar_feedbackhero_avaliacao_reservas: false,
};

const defaultNotificationSettings = {
  notificar_feedback_ruim_whatsapp: false,
  telefone_notificacao_feedback_ruim: '',
};

const fetchFeedbackHeroSettings = async () => {
  const res = await api.get('/feedback/settings');
  return res.data || {};
};

const updateFeedbackHeroSettings = async (data) => {
  const res = await api.put('/feedback/settings', data);
  return res.data || {};
};

const FeedbackHeroSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasProductAccess } = useAuthSession();

  const hasAtendabotProduct = hasProductAccess('atendabot');
  const hasReservoProduct = hasProductAccess('reservo');
  const hasAtendabotWhatsappContractLink = Boolean(ATENDABOT_WHATSAPP_CONTRACT_URL);
  const hasReservoWhatsappContractLink = Boolean(RESERVO_WHATSAPP_CONTRACT_URL);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [atendabotSettings, setAtendabotSettings] = useState(defaultSettings);
  const [reservoSettings, setReservoSettings] = useState(defaultReservoSettings);
  const [notificationSettings, setNotificationSettings] = useState(defaultNotificationSettings);
  const [googleLink, setGoogleLink] = useState('');
  const [googleIntegrationStatus, setGoogleIntegrationStatus] = useState(null);
  const [googleIntegrationChecked, setGoogleIntegrationChecked] = useState(false);
  const [googleIntegrationError, setGoogleIntegrationError] = useState(false);
  const [googleIntegrationOpen, setGoogleIntegrationOpen] = useState(false);
  const [checkoutProductKey, setCheckoutProductKey] = useState(null);

  useEffect(() => {
    let active = true;
    getGoogleBusinessStatus()
      .then((result) => {
        if (!active) return;
        setGoogleIntegrationStatus(result);
        setGoogleIntegrationError(false);
      })
      .catch(() => {
        if (active) setGoogleIntegrationError(true);
      })
      .finally(() => {
        if (active) setGoogleIntegrationChecked(true);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (searchParams.get('integracao') === 'google' || searchParams.has('google')) {
      setGoogleIntegrationOpen(true);
    }
  }, [searchParams]);

  const handleCloseGoogleIntegration = useCallback(() => {
    setGoogleIntegrationOpen(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('integracao');
    nextParams.delete('google');
    nextParams.delete('reason');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleGoogleStatusChange = useCallback((nextStatus) => {
    setGoogleIntegrationStatus(nextStatus);
    setGoogleIntegrationChecked(true);
    setGoogleIntegrationError(false);
  }, []);

  useEffect(() => {
    let active = true;

    const loadSettings = async () => {
      try {
        setLoading(true);
        const [atendabotData, reservoData, feedbackHeroData, googleLinkData] = await Promise.all([
          hasAtendabotProduct ? fetchAtendabotReservasSettings() : Promise.resolve(null),
          hasReservoProduct ? fetchRestaurantSettings() : Promise.resolve(null),
          fetchFeedbackHeroSettings(),
          getGoogleLink(),
        ]);
        if (!active) return;

        setAtendabotSettings(hasAtendabotProduct ? {
          usar_reservo_reservas: Boolean(atendabotData?.usar_reservo_reservas),
          usar_feedbackhero_avaliacao_pedidos: Boolean(atendabotData?.usar_feedbackhero_avaliacao_pedidos),
          link_reserva_externo: String(atendabotData?.link_reserva_externo ?? ''),
          reservo_link_reservas: String(atendabotData?.reservo_link_reservas ?? ''),
        } : defaultSettings);

        setReservoSettings(hasReservoProduct ? {
          usar_feedbackhero_avaliacao_reservas: Boolean(reservoData?.usar_feedbackhero_avaliacao_reservas),
        } : defaultReservoSettings);

        setNotificationSettings({
          notificar_feedback_ruim_whatsapp: Boolean(feedbackHeroData?.notificar_feedback_ruim_whatsapp),
          telefone_notificacao_feedback_ruim: String(feedbackHeroData?.telefone_notificacao_feedback_ruim ?? ''),
        });
        setGoogleLink(String(googleLinkData?.google_review_url ?? ''));
      } catch (error) {
        console.error('Erro ao carregar configurações do Feedback Hero:', error);
        toast.error(getApiErrorMessage(error, 'Não foi possível carregar as configurações.'));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSettings();
    return () => {
      active = false;
    };
  }, [hasAtendabotProduct, hasReservoProduct]);

  const handleSave = async () => {
    const notificationPhone = String(notificationSettings.telefone_notificacao_feedback_ruim || '').trim();
    const trimmedGoogleLink = String(googleLink || '').trim();
    if (notificationSettings.notificar_feedback_ruim_whatsapp && !notificationPhone) {
      toast.error('Informe o WhatsApp que recebera os alertas.');
      return;
    }

    if (trimmedGoogleLink) {
      try {
        const parsedGoogleLink = new URL(trimmedGoogleLink);
        if (!['http:', 'https:'].includes(parsedGoogleLink.protocol)) throw new Error('invalid protocol');
      } catch {
        toast.error('Informe um link válido do Google, começando com http:// ou https://.');
        return;
      }
    }

    try {
      setSaving(true);
      const payloadAtendabot = {
        usar_reservo_reservas: atendabotSettings.usar_reservo_reservas === true,
        usar_feedbackhero_avaliacao_pedidos:
          atendabotSettings.usar_feedbackhero_avaliacao_pedidos === true,
        link_reserva_externo: String(atendabotSettings.link_reserva_externo || '').trim(),
      };
      const payloadReservo = {
        usar_feedbackhero_avaliacao_reservas:
          reservoSettings.usar_feedbackhero_avaliacao_reservas === true,
      };
      const payloadNotification = {
        notificar_feedback_ruim_whatsapp:
          notificationSettings.notificar_feedback_ruim_whatsapp === true,
        telefone_notificacao_feedback_ruim: notificationPhone,
      };

      const [savedAtendabot, savedReservo, savedNotification, savedGoogleLink] = await Promise.all([
        hasAtendabotProduct
          ? updateAtendabotReservasSettings(payloadAtendabot)
          : Promise.resolve(null),
        hasReservoProduct
          ? updateRestaurantSettings(payloadReservo)
          : Promise.resolve(null),
        updateFeedbackHeroSettings(payloadNotification),
        patchGoogleLink({ google_review_url: trimmedGoogleLink }),
      ]);

      if (hasAtendabotProduct) {
        setAtendabotSettings((prev) => ({
          ...prev,
          usar_reservo_reservas: Boolean(savedAtendabot?.usar_reservo_reservas),
          usar_feedbackhero_avaliacao_pedidos: Boolean(savedAtendabot?.usar_feedbackhero_avaliacao_pedidos),
          link_reserva_externo: String(savedAtendabot?.link_reserva_externo ?? prev.link_reserva_externo ?? ''),
          reservo_link_reservas: String(
            savedAtendabot?.reservo_link_reservas ?? savedAtendabot?.link_reservas ?? prev.reservo_link_reservas ?? '',
          ),
        }));
      }

      if (hasReservoProduct) {
        setReservoSettings({
          usar_feedbackhero_avaliacao_reservas: Boolean(savedReservo?.usar_feedbackhero_avaliacao_reservas),
        });
      }

      toast.success('Configurações salvas com sucesso!');
      setNotificationSettings({
        notificar_feedback_ruim_whatsapp: Boolean(savedNotification?.notificar_feedback_ruim_whatsapp),
        telefone_notificacao_feedback_ruim: String(savedNotification?.telefone_notificacao_feedback_ruim ?? ''),
      });
      const persistedGoogleLink = String(savedGoogleLink?.google_review_url ?? '');
      setGoogleLink(persistedGoogleLink);
      try {
        if (persistedGoogleLink) localStorage.setItem('googleLink', persistedGoogleLink);
        else localStorage.removeItem('googleLink');
      } catch {
        // localStorage pode estar indisponível (modo privado / quota)
      }
    } catch (error) {
      console.error('Erro ao salvar configurações do Feedback Hero:', error);
      toast.error(getApiErrorMessage(error, 'Não foi possível salvar as configurações.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-2 sm:px-6 pb-12 sm:pb-16">
        <div
          className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden"
          style={{
            borderColor: 'rgba(15, 23, 42, 0.06)',
            background:
              'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
          }}
        >
          <div className="rounded-2xl border border-slate-200/70 bg-white p-6 text-sm text-slate-500 shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
            Carregando configurações…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-6 pb-12 sm:pb-16">
      <div
        className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden space-y-6"
        style={{
          borderColor: 'rgba(15, 23, 42, 0.06)',
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
      <section className="flex flex-col items-center text-center gap-1.5 px-2 pt-2 pb-3">
        <div className="flex items-center gap-2">
          <Settings size={22} className="text-[#1F5C3A]" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            Configurações do Feedback Hero
          </h2>
        </div>
        <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
          Controle quais produtos pedem avaliação com estrelas e enviam as respostas para o Feedback Hero.
        </p>
      </section>

      <section className="rounded-2xl border border-rose-200/70 bg-white p-6 text-slate-900 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
            <BellRing size={26} />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold">Aviso de avaliação negativa no WhatsApp</p>
            <p className="text-xs text-slate-600">
              Receba um aviso no WhatsApp sempre que um cliente deixar uma nota baixa.
            </p>
          </div>

          <span className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={notificationSettings.notificar_feedback_ruim_whatsapp}
              onClick={() => setNotificationSettings((prev) => ({
                ...prev,
                notificar_feedback_ruim_whatsapp: !prev.notificar_feedback_ruim_whatsapp,
              }))}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2 ${
                notificationSettings.notificar_feedback_ruim_whatsapp
                  ? 'bg-rose-500'
                  : 'bg-slate-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                  notificationSettings.notificar_feedback_ruim_whatsapp
                    ? 'translate-x-[1.375rem]'
                    : 'translate-x-0.5'
                }`}
              />
            </button>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
              notificationSettings.notificar_feedback_ruim_whatsapp
                ? 'bg-rose-100 text-rose-800'
                : 'bg-slate-100 text-slate-700'
            }`}
            >
              {notificationSettings.notificar_feedback_ruim_whatsapp ? 'Ativo' : 'Inativo'}
            </span>
          </span>
        </div>

        <div className="mt-5 grid gap-4">
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <Phone size={13} />
              WhatsApp para receber alerta
            </span>
            <input
              type="tel"
              value={notificationSettings.telefone_notificacao_feedback_ruim}
              onChange={(event) => setNotificationSettings((prev) => ({
                ...prev,
                telefone_notificacao_feedback_ruim: event.target.value,
              }))}
              placeholder="Ex: 51999999999"
              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            />
          </label>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Se deixar em branco, usaremos o número cadastrado do restaurante. Informe aqui o WhatsApp que deve receber os alertas de avaliações negativas.
        </p>
      </section>

      <section className="rounded-2xl border border-sky-200/70 bg-white p-6 text-slate-900 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 text-sky-700">
            <LinkIcon size={26} />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold">Link de avaliação no Google</p>
            <p className="text-xs text-slate-600">
              Clientes que derem 5 estrelas verão este link ao finalizar o formulário.
            </p>
          </div>

          {googleLink && (
            <button
              type="button"
              onClick={() => openExternalLink(googleLink)}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800 transition hover:bg-sky-100"
            >
              <ExternalLink size={14} />
              Abrir link
            </button>
          )}
        </div>

        <label className="mt-5 block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            URL da avaliação
          </span>
          <input
            type="url"
            value={googleLink}
            onChange={(event) => setGoogleLink(event.target.value)}
            placeholder="https://g.page/r/.../review"
            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
          />
        </label>

        <p className="mt-3 text-xs text-slate-500">
          O link fica salvo para o restaurante e será usado automaticamente no fim do formulário público.
        </p>
      </section>

      <section className="rounded-2xl border border-blue-200/80 bg-white p-6 text-slate-900 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-emerald-50 p-3 text-amber-500">
            <Star size={27} className="fill-amber-400 text-amber-400" />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold">Avaliações do Google</p>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                googleIntegrationStatus?.connected && googleIntegrationStatus?.location_selected
                  ? 'bg-emerald-100 text-emerald-800'
                  : googleIntegrationStatus?.connected
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-100 text-slate-600'
              }`}>
                {!googleIntegrationChecked
                  ? 'Verificando…'
                  : googleIntegrationError
                    ? 'Consulta indisponível'
                    : googleIntegrationStatus?.connected && googleIntegrationStatus?.location_selected
                      ? 'Conectado'
                      : googleIntegrationStatus?.connected
                        ? 'Escolha a ficha'
                        : 'Não conectado'}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-slate-600">
              Conecte sua ficha para consultar as avaliações do Google junto aos demais canais no painel.
            </p>
            {googleIntegrationStatus?.location_selected && (
              <p className="text-xs font-semibold text-emerald-700">
                {googleIntegrationStatus.location_display_name || 'Ficha do restaurante selecionada'}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setGoogleIntegrationOpen(true)}
            className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-xl bg-[#1a73e8] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#155fc0]"
          >
            {googleIntegrationStatus?.connected && googleIntegrationStatus?.location_selected
              ? 'Gerenciar integração'
              : 'Configurar integração'}
          </button>
        </div>
      </section>

      <section
        className="rounded-2xl border border-[#4bb17f]/35 p-6 text-white shadow-2xl"
        style={{ background: 'linear-gradient(135deg, #3127a0 0%, #2f8f6b 100%)' }}
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-2xl border border-[#74c79b]/45 bg-[#3127a0] p-2 shadow-lg">
            <img
              src={ATENDABOT_PRODUCT_IMAGE}
              alt="Atendabot"
              className="h-14 w-14 rounded-xl bg-transparent p-1 object-contain"
            />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold">Atendabot + Feedback Hero</p>
            <p className="text-xs text-white/90">
              Ative para a IA pedir nota por estrelas e um comentário breve ao finalizar pedidos.
            </p>
            <p className="text-[11px] text-white/80">
              A configuração é compartilhada com a tela de Prompt do Atendabot.
            </p>
          </div>

          {hasAtendabotProduct ? (
            <button
              type="button"
              onClick={() => navigate('/atendabot')}
              className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-xl border border-[#74c79b]/55 bg-[#2b2d8f]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#2b2d8f]/85"
            >
              <MessageCircleMore size={14} />
              Abrir Atendabot
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCheckoutProductKey('atendabot')}
              className="ml-auto inline-flex shrink-0 items-center rounded-xl border border-[#74c79b]/55 bg-[#2b2d8f]/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#2b2d8f]/85 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Ativar Atendabot
            </button>
          )}
        </div>

        {hasAtendabotProduct ? (
          <div className="mt-5 rounded-xl border border-white/20 bg-black/15 p-4">
            <label className="flex cursor-pointer items-start justify-between gap-4">
              <span>
                <span className="block text-sm font-semibold">Pedir avaliação dos pedidos finalizados</span>
                <span className="mt-1 block text-xs text-white/85">
                  Quando ativo, o cliente escolhe de 1 a 5 estrelas e depois pode enviar um comentário.
                </span>
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={atendabotSettings.usar_feedbackhero_avaliacao_pedidos}
                  onClick={() => setAtendabotSettings((prev) => ({
                    ...prev,
                    usar_feedbackhero_avaliacao_pedidos: !prev.usar_feedbackhero_avaliacao_pedidos,
                  }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                    atendabotSettings.usar_feedbackhero_avaliacao_pedidos
                      ? 'bg-emerald-400'
                      : 'bg-white/30'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                      atendabotSettings.usar_feedbackhero_avaliacao_pedidos
                        ? 'translate-x-[1.375rem]'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  atendabotSettings.usar_feedbackhero_avaliacao_pedidos
                    ? 'bg-emerald-200 text-emerald-900'
                    : 'bg-slate-700 text-slate-100'
                }`}
                >
                  {atendabotSettings.usar_feedbackhero_avaliacao_pedidos ? 'Ativo' : 'Inativo'}
                </span>
              </span>
            </label>
          </div>
        ) : (
          <div className="mt-4 space-y-3 rounded-xl border border-white/20 bg-black/15 p-4">
            <p className="text-xs text-white/90">
              Este restaurante ainda não tem acesso ao Atendabot. Contrate para liberar o fluxo automático de avaliação no Feedback Hero.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCheckoutProductKey('atendabot')}
                className="rounded-lg border border-[#74c79b]/55 bg-[#2b2d8f]/55 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2b2d8f]/75 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Ativar Atendabot
              </button>
              <button
                type="button"
                disabled={!hasAtendabotWhatsappContractLink}
                onClick={() => openExternalLink(ATENDABOT_WHATSAPP_CONTRACT_URL)}
                className="ml-auto rounded-lg border border-[#74c79b]/55 bg-[#2b2d8f]/55 px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2b2d8f]/75 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {hasAtendabotWhatsappContractLink
                  ? 'Falar no WhatsApp'
                  : 'Falar no WhatsApp (indisponível)'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section
        className="rounded-2xl border border-amber-200/45 p-6 text-slate-950 shadow-2xl"
        style={{ background: 'linear-gradient(135deg, #fff7d6 0%, #f7d68c 100%)' }}
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-2xl border border-amber-500/50 bg-[#ffdb2f] p-2 shadow-lg">
            <img
              src="/Reservo-icon.png"
              alt="Reservo"
              className="h-14 w-14 rounded-xl bg-transparent p-1 object-contain"
            />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-semibold">Reservo + Feedback Hero</p>
            <p className="text-xs text-slate-800">
              Ative para o Reservo pedir nota por estrelas no WhatsApp um dia após a reserva.
            </p>
            <p className="text-[11px] text-slate-700">
              A configuração é compartilhada com a tela de Configurações do Reservo.
            </p>
          </div>

          {hasReservoProduct ? (
            <button
              type="button"
              onClick={() => navigate('/reservo')}
              className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-xl border border-amber-500/45 bg-white/65 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-950 transition hover:bg-white"
            >
              <CalendarCheck2 size={14} />
              Abrir Reservo
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCheckoutProductKey('reservo')}
              className="ml-auto inline-flex shrink-0 items-center rounded-xl border border-amber-500/45 bg-white/65 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              Ativar Reservo
            </button>
          )}
        </div>

        {hasReservoProduct ? (
          <div className="mt-5 rounded-xl border border-amber-500/30 bg-white/45 p-4">
            <label className="flex cursor-pointer items-start justify-between gap-4">
              <span>
                <span className="block text-sm font-semibold">Pedir avaliação das reservas realizadas</span>
                <span className="mt-1 block text-xs text-slate-800">
                  Quando ativo, o cliente recebe botões de 1 a 5 estrelas pelo WhatsApp do Reservo no dia seguinte à reserva.
                </span>
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={reservoSettings.usar_feedbackhero_avaliacao_reservas}
                  onClick={() => setReservoSettings((prev) => ({
                    ...prev,
                    usar_feedbackhero_avaliacao_reservas: !prev.usar_feedbackhero_avaliacao_reservas,
                  }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                    reservoSettings.usar_feedbackhero_avaliacao_reservas
                      ? 'bg-emerald-500'
                      : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                      reservoSettings.usar_feedbackhero_avaliacao_reservas
                        ? 'translate-x-[1.375rem]'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  reservoSettings.usar_feedbackhero_avaliacao_reservas
                    ? 'bg-emerald-200 text-emerald-900'
                    : 'bg-slate-700 text-slate-100'
                }`}
                >
                  {reservoSettings.usar_feedbackhero_avaliacao_reservas ? 'Ativo' : 'Inativo'}
                </span>
              </span>
            </label>
          </div>
        ) : (
          <div className="mt-4 space-y-3 rounded-xl border border-amber-500/30 bg-white/45 p-4">
            <p className="text-xs text-slate-800">
              Este restaurante ainda não tem acesso ao Reservo. Contrate para liberar o fluxo automático de avaliação das reservas no Feedback Hero.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCheckoutProductKey('reservo')}
                className="rounded-lg border border-amber-400/70 bg-white px-3 py-2 text-xs font-semibold text-slate-950 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Ativar Reservo
              </button>
              <button
                type="button"
                disabled={!hasReservoWhatsappContractLink}
                onClick={() => openExternalLink(RESERVO_WHATSAPP_CONTRACT_URL)}
                className="ml-auto rounded-lg border border-amber-400/70 bg-white px-3 py-2 text-xs font-semibold text-slate-950 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {hasReservoWhatsappContractLink
                  ? 'Falar no WhatsApp'
                  : 'Falar no WhatsApp (indisponível)'}
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="flex justify-end items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2E8B57] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)] hover:shadow-[0_6px_16px_rgba(46,139,87,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? 'Salvando…' : 'Salvar configurações'}
          </button>
      </div>
      <ProductCheckoutModal
        open={Boolean(checkoutProductKey)}
        initialProductKey={checkoutProductKey}
        title="Ativar produtos"
        onClose={() => setCheckoutProductKey(null)}
      />
      <Dialog
        open={googleIntegrationOpen}
        onClose={handleCloseGoogleIntegration}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <GoogleReviewsSettings
            onClose={handleCloseGoogleIntegration}
            onStatusChange={handleGoogleStatusChange}
          />
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default FeedbackHeroSettings;
